import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Tooltip } from '@/components/ui/Tooltip'
import { cn } from '@/utils/cn'

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'ghost'
  | 'danger'
  /** Destructive on a light ground — the listing's bulk Delete. */
  | 'danger-outline'
  /** Destructive on a tinted ground — the discard dialog's "Discard Changes". */
  | 'danger-subtle'
  /** Tinted ground with a red edge — a destructive confirm that is not a delete (CMS Disable Page). */
  | 'danger-soft'
  | 'link'
  /** Filled grey — the design's Cancel next to a coloured primary action. */
  | 'neutral'
  /** Light blue — a secondary, non-committal action beside a Save. */
  | 'accent'
  /** Green — an action that makes something live (the CMS "Publish Changes"). */
  | 'success'
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl'

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  readonly variant?: ButtonVariant
  readonly size?: ButtonSize
  readonly isLoading?: boolean
  readonly leadingIcon?: ReactNode
  readonly trailingIcon?: ReactNode
  readonly fullWidth?: boolean
  readonly className?: string
  /**
   * Why the control is disabled. Shown in a `Tooltip` while `disabled` is set (a
   * browser shows no `title` on a disabled control); ignored when enabled.
   */
  readonly disabledReason?: string
}

/**
 * Filled variants keep their shape when disabled and turn flat grey with white
 * text, as the design's unavailable Save buttons do — rather than fading the
 * brand colour to a pale tint. `:disabled` outranks the base `bg-*` class, so
 * this wins without depending on stylesheet order (`cn` does not merge classes).
 */
const FILLED_DISABLED =
  'disabled:bg-border-strong disabled:text-fg-inverted disabled:border-transparent disabled:opacity-100'

const VARIANTS: Record<ButtonVariant, string> = {
  primary: cn(
    'bg-primary text-primary-fg hover:bg-primary-hover border border-transparent',
    FILLED_DISABLED,
  ),
  secondary: cn(
    'bg-secondary text-secondary-fg border border-transparent hover:bg-secondary-hover',
    FILLED_DISABLED,
  ),
  outline: 'bg-surface text-fg border border-border-strong hover:bg-surface-muted',
  ghost: 'bg-transparent text-fg border border-transparent hover:bg-surface-muted',
  danger: cn(
    'bg-danger text-fg-inverted border border-transparent hover:opacity-90',
    FILLED_DISABLED,
  ),
  /** White ground with a red edge and label, per the design's bulk Delete. */
  'danger-outline':
    'bg-surface text-danger-strong border border-danger-strong hover:bg-danger-subtle',
  /**
   * Tinted ground, red label — the softer destructive confirm the design uses
   * for discarding unsaved input, where a solid red reads as "delete a record".
   */
  'danger-subtle': cn(
    'bg-danger-subtle text-danger-strong border border-transparent hover:bg-danger-subtle hover:opacity-85',
    FILLED_DISABLED,
  ),
  'danger-soft': cn(
    'bg-danger-subtle text-danger-strong border border-danger-strong/40 hover:border-danger-strong',
    FILLED_DISABLED,
  ),
  link: 'bg-transparent text-primary border border-transparent underline underline-offset-2 hover:text-primary-hover',
  neutral: cn(
    'bg-neutral text-fg-inverted border border-transparent hover:opacity-90',
    FILLED_DISABLED,
  ),
  accent: cn(
    'bg-accent text-accent-fg border border-transparent hover:bg-accent-hover',
    FILLED_DISABLED,
  ),
  /**
   * A role, not a look: "this makes the change public". Distinct from `primary`
   * on purpose — the CMS editor puts Publish beside a neutral Save, and the two
   * must not read as the same commitment.
   */
  success: cn(
    'bg-success text-fg-inverted border border-transparent hover:opacity-90',
    FILLED_DISABLED,
  ),
}

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-11 px-5 text-sm gap-2',
  /** Prominent single-action CTA — the sign-in button. Matches the `lg` field height. */
  xl: 'h-11 px-5 text-sm gap-2',
}

/**
 * A button's classes, for an element that must look like one but is not a
 * `<button>` — an external `<a>` (a button nested in a link is invalid HTML).
 */
export function buttonClassName({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className,
}: Pick<ButtonProps, 'variant' | 'size' | 'fullWidth' | 'className'> = {}): string {
  return cn(
    'inline-flex items-center justify-center rounded-md font-medium transition-colors',
    'disabled:cursor-not-allowed disabled:opacity-50',
    VARIANTS[variant],
    SIZES[size],
    fullWidth && 'w-full',
    className,
  )
}

export function Button({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  leadingIcon,
  trailingIcon,
  fullWidth = false,
  className,
  disabled,
  disabledReason,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const button = (
    <button
      type={type}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      className={buttonClassName({ variant, size, fullWidth, className })}
      {...rest}
    >
      {isLoading ? <Spinner /> : leadingIcon}
      {children}
      {!isLoading && trailingIcon}
    </button>
  )
  // A busy button explains itself with its spinner; only a reasoned disable gets a tip.
  if (!disabled || isLoading || !disabledReason) return button
  return (
    <Tooltip content={disabledReason} className={fullWidth ? 'flex w-full' : 'inline-flex'}>
      {button}
    </Tooltip>
  )
}

/** In-button progress only. Never a full-screen loading state — use Skeleton. */
function Spinner() {
  return (
    <svg
      className="size-3.5 animate-spin"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
      <path
        d="M22 12a10 10 0 0 0-10-10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  )
}
